import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {object} WishlistProduct
 * @property {number} id
 * @property {string} title
 * @property {string} url
 * @property {string|null} image
 * @property {number} price
 */

const WISHLIST_STORAGE_KEY = 'casonda-wishlist';

/**
 * Event dispatched on `document` whenever the stored wishlist changes.
 * @extends {Event}
 */
export class WishlistUpdateEvent extends Event {
  /**
   * @param {WishlistProduct[]} items
   */
  constructor(items) {
    super(ThemeEvents.wishlistUpdate, { bubbles: true });
    this.detail = { items, count: items.length };
  }
}

/**
 * Thin wrapper around the localStorage-backed wishlist store.
 */
export const WishlistStore = {
  /**
   * @returns {WishlistProduct[]}
   */
  getItems() {
    try {
      const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch (error) {
      console.warn('[wishlist] Could not read wishlist:', error);
      return [];
    }
  },

  /**
   * @param {number} id
   * @returns {boolean}
   */
  has(id) {
    return this.getItems().some((item) => item.id === id);
  },

  /**
   * Adds the product when missing, removes it when already saved.
   * @param {WishlistProduct} product
   * @returns {{ added: boolean, items: WishlistProduct[] }}
   */
  toggle(product) {
    const items = this.getItems();
    const index = items.findIndex((item) => item.id === product.id);
    const added = index === -1;

    if (added) {
      items.push(product);
    } else {
      items.splice(index, 1);
    }

    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.warn('[wishlist] Could not persist wishlist:', error);
    }

    document.dispatchEvent(new WishlistUpdateEvent(items));

    return { added, items };
  },
};

/**
 * Floating heart button rendered on product cards.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} wishlistButton
 *
 * @extends {Component<Refs>}
 */
class CasondaWishlistCardButton extends Component {
  requiredRefs = ['wishlistButton'];

  /** @type {WishlistProduct|null} */
  #product = null;

  connectedCallback() {
    super.connectedCallback();

    this.#product = this.parseProduct();
    this.syncState();

    document.addEventListener(ThemeEvents.wishlistUpdate, this.syncState);
    window.addEventListener('storage', this.handleStorageEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.wishlistUpdate, this.syncState);
    window.removeEventListener('storage', this.handleStorageEvent);
  }

  /**
   * @returns {WishlistProduct|null}
   */
  parseProduct() {
    try {
      const product = JSON.parse(this.dataset.product ?? 'null');
      return product && typeof product.id === 'number' ? product : null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Keeps the visual and a11y state in sync with the stored wishlist.
   */
  syncState = () => {
    const saved = this.#product ? WishlistStore.has(this.#product.id) : false;
    const button = this.refs.wishlistButton;

    this.classList.toggle('is-active', saved);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', saved ? 'Remove from wishlist' : 'Add to wishlist');
  };

  /**
   * Toggles the product in the wishlist without navigating away.
   */
  handleToggle = () => {
    if (!this.#product) return;

    WishlistStore.toggle(this.#product);
  };

  /**
   * Re-syncs when another tab changes the wishlist.
   * @param {StorageEvent} event
   */
  handleStorageEvent = (event) => {
    if (event.key !== WISHLIST_STORAGE_KEY) return;

    this.syncState();
  };
}

/**
 * Header wishlist icon with a live count badge.
 *
 * @typedef {object} IconRefs
 * @property {HTMLElement} wishlistBubble
 * @property {HTMLElement} wishlistCount
 *
 * @extends {Component<IconRefs>}
 */
class CasondaWishlistIcon extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistCount'];

  connectedCallback() {
    super.connectedCallback();

    this.renderCount();

    document.addEventListener(ThemeEvents.wishlistUpdate, this.renderCount);
    window.addEventListener('pageshow', this.renderCount);
    window.addEventListener('storage', this.handleStorageEvent);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.wishlistUpdate, this.renderCount);
    window.removeEventListener('pageshow', this.renderCount);
    window.removeEventListener('storage', this.handleStorageEvent);
  }

  /**
   * Renders the badge count. Hidden while the wishlist is empty.
   */
  renderCount = () => {
    const count = WishlistStore.getItems().length;
    const hasItems = count > 0;

    this.refs.wishlistCount.textContent = count < 100 ? String(count) : '99+';
    this.refs.wishlistCount.classList.toggle('hidden', !hasItems);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', !hasItems);
    this.classList.toggle('csnd-wishlist-icon--has-items', hasItems);
  };

  /**
   * Re-renders when another tab changes the wishlist.
   * @param {StorageEvent} event
   */
  handleStorageEvent = (event) => {
    if (event.key !== WISHLIST_STORAGE_KEY) return;

    this.renderCount();
  };
}

if (!customElements.get('casonda-wishlist-card-button')) {
  customElements.define('casonda-wishlist-card-button', CasondaWishlistCardButton);
}

if (!customElements.get('casonda-wishlist-icon')) {
  customElements.define('casonda-wishlist-icon', CasondaWishlistIcon);
}
